/**
 * The Files tree's view-model.
 *
 * The server answers one directory at a time (name, kind, whether it opens
 * onto anything), so the client owns the shape those answers become: which
 * rows are visible for a set of expanded directories, and which expanded
 * directories still owe a fetch. Collapsing keeps a directory's listing
 * cached; re-expanding is free and does not re-fetch.
 *
 * A leaf module on purpose: the pane renders it, the spec pins it, and
 * neither needs React to test the walk.
 */
import type { WorkspaceDirectoryEntry } from "@cmmd-forge/contracts";

/** One directory's children, keyed by its workspace-relative path ("" is the root). */
export type FilesTreeListing = Readonly<Record<string, readonly WorkspaceDirectoryEntry[]>>;

export interface FilesTreeRow {
  /** Workspace-relative path; a directory row's key for expand state and fetches. */
  readonly relativePath: string;
  readonly name: string;
  readonly kind: WorkspaceDirectoryEntry["kind"];
  /** Directories only: the server already knows whether this one opens onto anything. */
  readonly hasChildren: boolean;
  readonly depth: number;
  /** Directories only: whether the row's children are showing. */
  readonly expanded: boolean;
}

export function visibleFilesTreeRows(input: {
  readonly listings: FilesTreeListing;
  readonly expanded: ReadonlySet<string>;
}): readonly FilesTreeRow[] {
  const rows: FilesTreeRow[] = [];
  const visit = (directoryPath: string, depth: number) => {
    for (const entry of input.listings[directoryPath] ?? []) {
      const relativePath =
        directoryPath.length === 0 ? entry.name : `${directoryPath}/${entry.name}`;
      const expanded = input.expanded.has(relativePath);
      rows.push({
        relativePath,
        name: entry.name,
        kind: entry.kind,
        hasChildren: entry.hasChildren,
        depth,
        expanded: entry.kind === "directory" ? expanded : false,
      });
      if (entry.kind === "directory" && expanded) visit(relativePath, depth + 1);
    }
  };
  visit("", 0);
  return rows;
}

/**
 * Expanded directories with no listing yet, in tree order.
 *
 * The pane fetches exactly these: expanding a directory is one RPC, not a
 * walk, and a reload resumes with the root alone until each level is opened.
 */
export function pendingExpandedDirectories(input: {
  readonly listings: FilesTreeListing;
  readonly expanded: ReadonlySet<string>;
}): readonly string[] {
  return [...input.expanded].filter((path) => input.listings[path] === undefined).toSorted();
}
